/**
 * Token 管理配置
 * 基于 js-cookie 的 Token 存储管理
 */

import Cookies from 'js-cookie'

/**
 * Token 管理器
 */
export class TokenManager {
  private tokenKey: string

  constructor(tokenKey: string = 'ct_token') {
    this.tokenKey = tokenKey
  }

  /**
   * 获取 Token
   */
  get(): string | undefined {
    return Cookies.get(this.tokenKey)
  }

  /**
   * 设置 Token
   */
  set(token: string, days: number = 7): void {
    const options: Cookies.CookieAttributes = {
      expires: days,
      path: '/',
      sameSite: 'lax'
    }

    // HTTPS 环境下添加 secure 标志
    if (window.location.protocol === 'https:') {
      options.secure = true
    }

    // 生产环境可以考虑添加 httpOnly (需要后端配合)
    // if (import.meta.env.PROD) {
    //   options.httpOnly = true
    // }

    Cookies.set(this.tokenKey, token, options)
  }

  /**
   * 删除 Token
   * 从 cookies 中删除
   */
  remove(): void {
    Cookies.remove(this.tokenKey, { path: '/' })
  }

  /**
   * 检查 Token 是否存在
   */
  exists(): boolean {
    return !!this.get()
  }

  /**
   * 获取 Token 键名
   */
  getTokenKey(): string {
    return this.tokenKey
  }
}

/**
 * 获取基于应用类型的 Token 管理器实例
 *
 * 根据环境变量 VITE_APP_TYPE 创建对应的 Token 管理器，
 * 确保不同应用类型的 token 存储隔离。
 *
 * Cookie key 格式：ct_token_{appType}
 * - 客户端：ct_token_client
 * - 运营端：ct_token_admin
 *
 * @returns {TokenManager} Token管理器实例
 */
export function getTokenManager(): TokenManager {
  try {
    // 检查当前路由是否为匿名访问页面
    const currentPath = window.location.pathname
    const isAnonymousAccess = currentPath.includes('/anonymous-access')
      || currentPath.includes('/external')
      // 运输委托询舱确认是供应商外链，必须与员工会话使用不同 Cookie。
      || currentPath.includes('/supplier/space-booking/')
      || currentPath.includes('/bidding/supplier/bid')

    // 根据页面类型返回不同的 TokenManager
    if (isAnonymousAccess) {
      return new TokenManager('ct_anonymous-access_token')
    }
  } catch (error) {
    console.error('getTokenManager失败:', error)
    return null
  }

  // 动态获取应用类型，避免循环依赖
  const getAppType = (): 'client' | 'admin' => {
    const envAppType = import.meta.env.VITE_APP_TYPE
    if (envAppType === 'client' || envAppType === 'admin') {
      return envAppType
    }
    if (typeof (globalThis as any).__APP_TYPE__ !== 'undefined') {
      return (globalThis as any).__APP_TYPE__
    }
    return 'client'
  }

  const appType = getAppType()
  return new TokenManager(`ct_token_${appType}`)
}

// 创建默认的 Token 管理器实例（保持向后兼容）
export const tokenManager = getTokenManager()
